import {
  IBrandOptions,
  IClientNewsfeedData,
  INewsfeed,
  INewsfeedData,
  INewsfeedItemData,
} from '@common/interfaces/newsfeed'
import { INote, INotesData } from '@common/interfaces/notes'
import { combineReducers } from 'redux'
import { createLoadingDataReducer } from '../../helpers/redux'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import { ADD_NOTE_SUCCESS, DELETE_NOTE_SUCCESS, UPDATE_NOTE_SUCCESS } from '../notes/actions'
import { insertReply, removeFromTree, updateInTree } from '../notes/threadHelpers'
import { ILoadingData } from '../types'
import * as actions from './actions'

interface IWrongMatchReport {
  newsfeedId: string
  totalReports: number
  success: {
    message: string
  }
}

interface ICreateLeadResult {
  leadId: string
  success: {
    message: string
  }
}

const baseNewsfeedDataReducer = createLoadingDataReducer<INewsfeedData>(
  [actions.LIST_NEWSFEED_REQUEST],
  [actions.UPDATE_NEWSFEED_REQUEST, actions.DELETE_NEWSFEED_REQUEST],
  true,
)

const ITEM_PATCHING_ACTIONS: string[] = [
  actions.CREATE_LEAD_SUCCESS,
  actions.REPORT_WRONG_MATCH_SUCCESS,
  actions.UPDATE_NEWSFEED_SUCCESS,
]

/**
 * How a note write moves a headline's reply badge, or null when it doesn't move one.
 *
 * Only a TOP-LEVEL note counts: a reply to a reply is reached through its root,
 * and `ListNewsfeed.attachReplyCounts` counts the same set, so the badge stays
 * consistent with both the thread and the next refetch.
 */
const getReplyCountChange = (
  action: IAction<unknown>,
): { newsfeedHeadlineId: string; delta: number } | null => {
  if (action.type !== ADD_NOTE_SUCCESS && action.type !== DELETE_NOTE_SUCCESS) {
    return null
  }
  const note = action.data as { newsfeedHeadlineId?: string | null; parentId?: string | null }
  if (!note?.newsfeedHeadlineId || note.parentId) {
    return null
  }
  return {
    newsfeedHeadlineId: note.newsfeedHeadlineId,
    delta: action.type === ADD_NOTE_SUCCESS ? 1 : -1,
  }
}

// These return the updated headline in `items` — patch it into the loaded list so
// the row (linked-account chips, client/prospect badges, the reply badge) updates
// in place instead of refetching the whole list (which would reset the scroll position)
const newsfeedData = (
  state: ILoadingData<INewsfeedData> | undefined,
  action: IAction<{ items?: INewsfeed[] }>,
): ILoadingData<INewsfeedData> => {
  const nextState = baseNewsfeedDataReducer(state, action)

  if (!nextState.data?.data) {
    return nextState
  }

  const replyCountChange = getReplyCountChange(action)
  if (replyCountChange) {
    const { newsfeedHeadlineId, delta } = replyCountChange
    return {
      ...nextState,
      data: {
        ...nextState.data,
        data: nextState.data.data.map((item) =>
          item.id === newsfeedHeadlineId
            ? { ...item, replyCount: Math.max(0, (item.replyCount || 0) + delta) }
            : item,
        ),
      },
    }
  }

  if (!ITEM_PATCHING_ACTIONS.includes(action.type)) {
    return nextState
  }

  const updatedItems = action.data?.items?.filter(Boolean)
  if (!updatedItems?.length) {
    return nextState
  }

  return {
    ...nextState,
    data: {
      ...nextState.data,
      data: nextState.data.data.map(
        (item) => updatedItems.find(({ id }) => id === item.id) || item,
      ),
    },
  }
}

type HeadlineReplies = Record<string, INotesData>
type IHeadlineNote = INote & { newsfeedHeadlineId?: string | null }

const patchThread = (
  state: HeadlineReplies,
  headlineId: string | null | undefined,
  patch: (thread: INotesData) => INotesData,
): HeadlineReplies => {
  const thread = headlineId ? state[headlineId] : undefined
  return thread && headlineId ? { ...state, [headlineId]: patch(thread) } : state
}

// Inline headline threads (Market News rows, the sidebar News tab), keyed by headline so they
// never share the notes slice the page's own notes panel reads.
const headlineReplies = (
  state: HeadlineReplies = {},
  action: IActionWithParams<IPaginationParams> & { id?: string },
): HeadlineReplies => {
  switch (action.type) {
    case actions.LIST_HEADLINE_REPLIES_SUCCESS: {
      const data = action.data as INotesData
      const existing = action.id ? state[action.id] : undefined
      if (!action.id) return state
      return {
        ...state,
        [action.id]:
          action.params?.loadMore && existing
            ? { ...existing, data: [...existing.data, ...data.data] }
            : data,
      }
    }
    case ADD_NOTE_SUCCESS: {
      const note = action.data as IHeadlineNote
      return patchThread(state, note?.newsfeedHeadlineId, (thread) =>
        note.parentId
          ? { ...thread, data: insertReply(thread.data, note) }
          : { ...thread, total: thread.total + 1, data: [note, ...thread.data] },
      )
    }
    case UPDATE_NOTE_SUCCESS: {
      const note = action.data as IHeadlineNote
      return patchThread(state, note?.newsfeedHeadlineId, (thread) => ({
        ...thread,
        data: updateInTree(thread.data, note),
      }))
    }
    case DELETE_NOTE_SUCCESS: {
      const note = action.data as IHeadlineNote
      return patchThread(state, note?.newsfeedHeadlineId, (thread) => ({
        ...thread,
        total: note.parentId ? thread.total : Math.max(0, thread.total - 1),
        data: removeFromTree(thread.data, note.id),
      }))
    }
    default:
      return state
  }
}

export default combineReducers({
  newsfeedData,
  headlineReplies,
  clientNewsfeedData: createLoadingDataReducer<IClientNewsfeedData>([
    actions.LIST_CLIENT_NEWSFEED_REQUEST,
  ]),

  brandOptions: createLoadingDataReducer<IBrandOptions>([actions.LIST_BRAND_OPTIONS_REQUEST]),

  newsfeedItem: createLoadingDataReducer<INewsfeedItemData>([actions.SHOW_NEWSFEED_REQUEST]),

  wrongMatchReport: createLoadingDataReducer<IWrongMatchReport>([
    actions.REPORT_WRONG_MATCH_REQUEST,
  ]),

  createLeadResult: createLoadingDataReducer<ICreateLeadResult>([actions.CREATE_LEAD_REQUEST]),
})
